/**
 * Feedback Service
 * Handles CRUD operations for feedback and feature suggestions
 */

import { supabase } from '@/lib/supabase';
import { notifyNewFeedback, notifyNewFeatureSuggestion, notifyFeedbackResolved } from './email.service';
import type {
  Feedback,
  FeedbackFormData,
  FeedbackStatus,
  FeedbackWithUser,
  FeatureSuggestion,
  FeatureSuggestionFormData,
  FeatureSuggestionStatus,
  BrowserInfo,
} from '@/types/feedback';

// Admin email(s) to notify - configure in environment or settings
const FEEDBACK_NOTIFICATION_EMAILS = ['ryan@elevationcg.com'];

// =============================================================================
// FEEDBACK CRUD
// =============================================================================

export async function getMyFeedback(userEmail: string): Promise<Feedback[]> {
  const { data, error } = await supabase
    .from('feedback')
    .select('id, user_name, user_email, page_title, page_url, feedback_type, description, status, admin_notes, created_at, updated_at')
    .eq('user_email', userEmail)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[getMyFeedback] Error:', error);
    throw error;
  }

  return (data || []) as unknown as Feedback[];
}

export async function getMyFeatureSuggestions(userEmail: string): Promise<FeatureSuggestion[]> {
  const { data, error } = await supabase
    .from('feature_suggestions')
    .select('id, user_name, user_email, title, description, category, priority, status, admin_notes, created_at, updated_at')
    .eq('user_email', userEmail)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[getMyFeatureSuggestions] Error:', error);
    throw error;
  }

  return (data || []) as unknown as FeatureSuggestion[];
}

export async function getAllFeedback(): Promise<Feedback[]> {
  const { data, error } = await supabase
    .from('feedback')
    .select('id, user_name, user_email, page_title, page_url, feedback_type, description, status, admin_notes, created_at, updated_at')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[getAllFeedback] Error:', error);
    throw error;
  }

  return (data || []) as unknown as Feedback[];
}

export async function getAllFeatureSuggestions(): Promise<FeatureSuggestion[]> {
  const { data, error } = await supabase
    .from('feature_suggestions')
    .select('id, user_name, user_email, title, description, category, priority, status, admin_notes, created_at, updated_at')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[getAllFeatureSuggestions] Error:', error);
    throw error;
  }

  return (data || []) as unknown as FeatureSuggestion[];
}

export async function getFeedbackList(options?: {
  status?: FeedbackStatus;
  limit?: number;
  offset?: number;
}): Promise<FeedbackWithUser[]> {
  let query = supabase
    .from('feedback')
    .select(`
      *,
      resolved_by_user:profiles!feedback_resolved_by_fkey(full_name, email)
    `)
    .order('created_at', { ascending: false });

  if (options?.status) {
    query = query.eq('status', options.status);
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[getFeedbackList] Error:', error);
    throw error;
  }

  return data || [];
}

export async function getFeedbackById(id: string): Promise<FeedbackWithUser | null> {
  const { data, error } = await supabase
    .from('feedback')
    .select(`
      *,
      resolved_by_user:profiles!feedback_resolved_by_fkey(full_name, email)
    `)
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    console.error('[getFeedbackById] Error:', error);
    throw error;
  }

  return data;
}

export async function createFeedback(
  formData: FeedbackFormData,
  user: { id: string; name?: string; email?: string },
  pageInfo: { url: string; title?: string },
  browserInfo: BrowserInfo
): Promise<Feedback> {
  const { data, error } = await supabase
    .from('feedback')
    .insert({
      user_id: user.id,
      user_name: user.name || null,
      user_email: user.email || null,
      page_url: pageInfo.url,
      page_title: pageInfo.title || null,
      feedback_type: formData.feedback_type,
      description: formData.description,
      screenshot_data: formData.screenshot_data || null,
      screenshot_width: formData.screenshot_width || null,
      screenshot_height: formData.screenshot_height || null,
      selected_region: formData.selected_region || null,
      browser_info: browserInfo,
      status: 'new',
    })
    .select()
    .single();

  if (error) {
    console.error('[createFeedback] Error:', error);
    throw error;
  }

  // Send email notification (async, don't block)
  notifyNewFeedback(
    {
      id: data.id,
      feedback_type: data.feedback_type,
      description: data.description,
      page_url: data.page_url,
      page_title: data.page_title || undefined,
      user_name: data.user_name || undefined,
      user_email: data.user_email || undefined,
      has_screenshot: !!data.screenshot_data,
    },
    FEEDBACK_NOTIFICATION_EMAILS
  ).catch((err) => console.error('[createFeedback] Email notification failed:', err));

  return data;
}

export async function updateFeedbackStatus(
  id: string,
  status: FeedbackStatus,
  adminNotes?: string,
  resolvedBy?: string
): Promise<Feedback> {
  const updates: Partial<Feedback> = { status };

  if (adminNotes !== undefined) {
    updates.admin_notes = adminNotes;
  }

  if (status === 'resolved' || status === 'wont_fix') {
    updates.resolved_by = resolvedBy || null;
    updates.resolved_at = new Date().toISOString();
  }

  const { data, error } = await supabase
    .from('feedback')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('[updateFeedbackStatus] Error:', error);
    throw error;
  }

  // Send resolved notification to the submitter (async, non-blocking)
  if (status === 'resolved' && data.user_email) {
    notifyFeedbackResolved({
      id: data.id,
      feedback_type: data.feedback_type,
      description: data.description,
      page_title: data.page_title ?? undefined,
      page_url: data.page_url ?? undefined,
      user_name: data.user_name ?? undefined,
      user_email: data.user_email,
      admin_notes: data.admin_notes ?? undefined,
      resolved_at: data.resolved_at ?? new Date().toISOString(),
    }).catch(err => console.error('[updateFeedbackStatus] Failed to send resolved notification:', err));
  }

  return data;
}

// =============================================================================
// FEATURE SUGGESTIONS CRUD
// =============================================================================

export async function getFeatureSuggestions(options?: {
  status?: FeatureSuggestionStatus;
  category?: string;
  limit?: number;
  offset?: number;
}): Promise<FeatureSuggestion[]> {
  let query = supabase
    .from('feature_suggestions')
    .select('*')
    .order('created_at', { ascending: false });

  if (options?.status) {
    query = query.eq('status', options.status);
  }

  if (options?.category) {
    query = query.eq('category', options.category);
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[getFeatureSuggestions] Error:', error);
    throw error;
  }

  return data || [];
}

export async function getFeatureSuggestionById(id: string): Promise<FeatureSuggestion | null> {
  const { data, error } = await supabase
    .from('feature_suggestions')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    console.error('[getFeatureSuggestionById] Error:', error);
    throw error;
  }

  return data;
}

export async function createFeatureSuggestion(
  formData: FeatureSuggestionFormData,
  user: { id: string; name?: string; email?: string }
): Promise<FeatureSuggestion> {
  const { data, error } = await supabase
    .from('feature_suggestions')
    .insert({
      user_id: user.id,
      user_name: user.name || null,
      user_email: user.email || null,
      title: formData.title,
      description: formData.description,
      category: formData.category,
      priority: formData.priority,
      status: 'submitted',
    })
    .select()
    .single();

  if (error) {
    console.error('[createFeatureSuggestion] Error:', error);
    throw error;
  }

  // Send email notification (async, don't block)
  notifyNewFeatureSuggestion(
    {
      id: data.id,
      title: data.title,
      description: data.description,
      category: data.category,
      priority: data.priority,
      user_name: data.user_name || undefined,
      user_email: data.user_email || undefined,
    },
    FEEDBACK_NOTIFICATION_EMAILS
  ).catch((err) => console.error('[createFeatureSuggestion] Email notification failed:', err));

  return data;
}

export async function updateFeatureSuggestionStatus(
  id: string,
  status: FeatureSuggestionStatus,
  adminNotes?: string
): Promise<FeatureSuggestion> {
  const updates: Partial<FeatureSuggestion> = { status };

  if (adminNotes !== undefined) {
    updates.admin_notes = adminNotes;
  }

  const { data, error } = await supabase
    .from('feature_suggestions')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('[updateFeatureSuggestionStatus] Error:', error);
    throw error;
  }

  return data;
}

// =============================================================================
// STATISTICS
// =============================================================================

export async function getFeedbackStats() {
  const { data, error } = await supabase
    .from('feedback')
    .select('status, feedback_type');

  if (error) {
    console.error('[getFeedbackStats] Error:', error);
    throw error;
  }

  const stats = {
    total: data.length,
    new: 0,
    reviewed: 0,
    in_progress: 0,
    resolved: 0,
    wont_fix: 0,
    by_type: {
      bug: 0,
      improvement: 0,
      confusion: 0,
      other: 0,
    },
  };

  data.forEach((item) => {
    const statusKey = item.status as FeedbackStatus;
    if (statusKey in stats) {
      stats[statusKey]++;
    }
    stats.by_type[item.feedback_type as keyof typeof stats.by_type]++;
  });

  return stats;
}

export async function getFeatureSuggestionStats() {
  const { data, error } = await supabase
    .from('feature_suggestions')
    .select('status, category');

  if (error) {
    console.error('[getFeatureSuggestionStats] Error:', error);
    throw error;
  }

  const stats = {
    total: data.length,
    submitted: 0,
    under_review: 0,
    planned: 0,
    in_progress: 0,
    completed: 0,
    declined: 0,
    by_category: {
      feature: 0,
      integration: 0,
      automation: 0,
      reporting: 0,
      ui_ux: 0,
      other: 0,
    },
  };

  data.forEach((item) => {
    const statusKey = item.status as FeatureSuggestionStatus;
    if (statusKey in stats) {
      stats[statusKey]++;
    }
    stats.by_category[item.category as keyof typeof stats.by_category]++;
  });

  return stats;
}
